import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useOrders } from '../context/OrderContext';
import { useAuth } from '../context/AuthContext';
import { useStripe } from '../context/StripeContext';
import { usePricing } from '../context/PricingContext';
import { CustomerInfo, Order } from '../types';
import SEO from '../components/SEO';
import { CreditCard, Package, Info } from 'lucide-react';
import Cookies from 'js-cookie';
import { trackEcommerce } from '../utils/analytics';

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { items, getTotalPrice, clearCart } = useCart();
  const { addOrder } = useOrders();
  const { user } = useAuth();
  const { stripe } = useStripe();
  const { pricingSettings } = usePricing();
  
  const subtotal = getTotalPrice();
  const minimumOrderAmount = pricingSettings.minimumOrderAmount || 20;
  const needsMinimumOrderFee = subtotal > 0 && subtotal < minimumOrderAmount;
  const minimumOrderFee = needsMinimumOrderFee ? minimumOrderAmount - subtotal : 0;
  const adjustedSubtotal = subtotal + minimumOrderFee;
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
  });
  const [paymentMethod, setPaymentMethod] = useState<string>('credit');
  const [orderNotes, setOrderNotes] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setCustomerInfo({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: localStorage.getItem(`user_${user.id}_phone`) || '',
        company: localStorage.getItem(`user_${user.id}_company`) || '',
        address: localStorage.getItem(`user_${user.id}_address`) || '',
        city: localStorage.getItem(`user_${user.id}_city`) || '',
        state: localStorage.getItem(`user_${user.id}_state`) || '',
        zipCode: localStorage.getItem(`user_${user.id}_zipCode`) || '',
      });
    }
  }, [user]);

  // Track checkout begin when page loads with items
  useEffect(() => {
    if (items.length > 0) {
      trackEcommerce.beginCheckout(items, adjustedSubtotal);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);
    
    try {
      // Create order items, including minimum order fee if needed
      const orderItems = [...items];
      if (needsMinimumOrderFee) {
        // Add a special item for the minimum order fee
        orderItems.push({
          id: `MIN-ORDER-FEE-${Date.now()}`,
          product: {
            id: 'minimum-order-fee',
            name: 'Minimum Order Fee',
            category: 'Fee',
            basePrice: minimumOrderFee,
            description: `Fee to meet minimum order requirement of $${minimumOrderAmount.toFixed(2)}`,
            image: '',
            customizable: false,
            materials: [],
            finishingOptions: [],
            colorSpecs: [],
          },
          width: 0,
          height: 0,
          quantity: 1,
          material: '',
          finishing: '',
          colorSpec: '',
          customText: '',
          price: minimumOrderFee,
        });
      }

      // Create order object
      const order: Order = {
        id: `ORDER-${Date.now()}`,
        items: orderItems,
        customerInfo,
        totalPrice: total, // Including tax
        status: 'pending' as const,
        createdAt: new Date(),
        updatedAt: new Date(),
        notes: orderNotes,
      };

      // Save order to get order ID
      const savedOrder = await addOrder(order);
      
      if (paymentMethod === 'credit' && stripe) {
        // Process credit card payment with Stripe
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const token = Cookies.get('authToken');
        
        // Create Stripe checkout session
        const response = await fetch(`${apiUrl}/payment/create-checkout-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            orderId: savedOrder.id,
            items: orderItems.map(item => ({
              productId: item.product.id,
              productName: item.product.name,
              size: item.width && item.height ? `${item.width}" × ${item.height}"` : 'N/A',
              material: item.material || 'N/A',
              finishing: item.finishing || 'N/A',
              quantity: item.quantity,
              price: item.price
            })),
            subtotal,
            tax,
            total
          })
        });

        if (!response.ok) {
          throw new Error('Failed to create checkout session');
        }

        const { sessionId, url } = await response.json();
        
        // Redirect to Stripe Checkout
        if (url) {
          window.location.href = url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else {
        // Handle pay at pickup
        // Track purchase event
        trackEcommerce.purchase(savedOrder.id, orderItems, total, tax);
        
        clearCart();
        navigate(`/order-confirmation?orderId=${savedOrder.id}&paymentMethod=pickup`);
      }
    } catch (error) {
      console.error('Failed to process order:', error);
      setError('Failed to process your order. Please try again.');
      setIsProcessing(false);
    }
  };

  if (items.length === 0) {
    navigate('/cart');
    return null;
  }

  const tax = adjustedSubtotal * pricingSettings.taxRate;
  const total = adjustedSubtotal + tax;

  return (
    <>
      <SEO 
        title="Checkout"
        description="Complete your order for professional sign and banner printing. Secure checkout process."
      />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold mb-8">Checkout</h1>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-semibold mb-4">Customer Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={customerInfo.firstName}
                      onChange={handleInputChange}
                      required
                      disabled={isProcessing}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-keystone-gold disabled:bg-gray-100"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={customerInfo.lastName}
                      onChange={handleInputChange}
                      required
                      disabled={isProcessing}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-keystone-gold disabled:bg-gray-100"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={customerInfo.email}
                      onChange={handleInputChange}
                      required
                      disabled={isProcessing}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-keystone-gold disabled:bg-gray-100"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={customerInfo.phone}
                      onChange={handleInputChange}
                      required
                      disabled={isProcessing}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-keystone-gold disabled:bg-gray-100"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={customerInfo.company}
                      onChange={handleInputChange}
                      disabled={isProcessing}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-keystone-gold disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-semibold mb-4">Billing Address</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={customerInfo.address}
                      onChange={handleInputChange}
                      required
                      disabled={isProcessing}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-keystone-gold disabled:bg-gray-100"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="md:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={customerInfo.city}
                        onChange={handleInputChange}
                        required
                        disabled={isProcessing}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-keystone-gold disabled:bg-gray-100"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State *
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={customerInfo.state}
                        onChange={handleInputChange}
                        required
                        maxLength={2}
                        disabled={isProcessing}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-keystone-gold disabled:bg-gray-100"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        name="zipCode"
                        value={customerInfo.zipCode}
                        onChange={handleInputChange}
                        required
                        pattern="[0-9]{5}(-[0-9]{4})?"
                        disabled={isProcessing}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-keystone-gold disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-semibold mb-4">Payment Method</h2>
                
                <div className="space-y-3">
                  <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="credit"
                      checked={paymentMethod === 'credit'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      disabled={isProcessing}
                      className="mr-3"
                    />
                    <CreditCard className="h-5 w-5 mr-3 text-gray-600" />
                    <div>
                      <p className="font-medium">Credit Card</p>
                      <p className="text-sm text-gray-600">Pay securely with Stripe</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="pickup"
                      checked={paymentMethod === 'pickup'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      disabled={isProcessing}
                      className="mr-3"
                    />
                    <Package className="h-5 w-5 mr-3 text-gray-600" />
                    <div>
                      <p className="font-medium">Pay at Pickup</p>
                      <p className="text-sm text-gray-600">Pay when you pick up your order</p>
                    </div>
                  </label>
                </div>
                
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order Notes
                  </label>
                  <textarea
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    rows={3}
                    disabled={isProcessing}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-keystone-gold disabled:bg-gray-100"
                    placeholder="Any special instructions for your order..."
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="bg-gray-100 rounded-lg p-6 sticky top-4">
                <h2 className="text-2xl font-bold mb-4">Order Summary</h2>
                
                <div className="space-y-3 mb-6">
                  {items.map((item) => (
                    <div key={item.id} className="text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">{item.product.name}</span>
                        <span>${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                      <div className="text-gray-600">
                        {item.width}" × {item.height}" • Qty: {item.quantity}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="space-y-2 mb-6 pt-6 border-t">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span className="font-medium">${subtotal.toFixed(2)}</span>
                  </div>
                  {needsMinimumOrderFee && (
                    <div className="flex justify-between text-yellow-600">
                      <span className="text-sm flex items-start">
                        <span>
                          Minimum order fee:
                          <span className="block text-xs text-gray-500">
                            (${minimumOrderAmount.toFixed(2)} minimum)
                          </span>
                        </span>
                        <div className="relative group ml-1">
                          <Info className="h-4 w-4 text-yellow-500 cursor-help" />
                          <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                            <div className="relative">
                              We require a minimum order of ${minimumOrderAmount.toFixed(2)} to help cover our production and handling costs. We apologize for any inconvenience and hope you understand. Thank you for your business!
                              <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                          </div>
                        </div>
                      </span>
                      <span className="font-medium">${minimumOrderFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Tax ({(pricingSettings.taxRate * 100).toFixed(0)}%):</span>
                    <span className="font-medium">${tax.toFixed(2)}</span>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total:</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isProcessing || (!stripe && paymentMethod === 'credit')}
                  className="w-full mt-6 bg-keystone-gold text-keystone-blue py-3 rounded-lg font-semibold hover:bg-keystone-gold-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Processing...' : (paymentMethod === 'credit' ? 'Proceed to Payment' : 'Place Order')}
                </button>
                
                {paymentMethod === 'credit' && (
                  <div className="mt-4 flex items-center justify-center text-sm text-gray-600">
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L2 7V12C2 16.5 4.23 20.68 7.62 23.15L12 26L16.38 23.15C19.77 20.68 22 16.5 22 12V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Secure payment powered by Stripe
                  </div>
                )}
                
                <p className="text-xs text-gray-600 mt-4 text-center">
                  By placing this order, you agree to our terms and conditions.
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default Checkout;